// frontend/src/modules/expenses/components/CategoryPicker.tsx
import * as React from 'react';
import { useApi } from '../../../design-system/useApi';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import type { Category } from '../types';

interface CategoryPickerProps {
  value: string | null;
  onChange: (categoryId: string) => void;
  /** Called with the full selected category (so the form can react to kind/rate). */
  onCategoryChange?: (category: Category | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function CategoryPicker({ value, onChange, onCategoryChange, disabled, placeholder }: CategoryPickerProps) {
  const { data, loading } = useApi<{ rows: Category[] }>('/api/expenses/categories');
  const categories = data?.rows ?? [];

  // Re-emit the resolved category whenever the value or list changes, so the
  // parent can drive a quantity field for rate-based categories.
  React.useEffect(() => {
    if (!onCategoryChange) return;
    onCategoryChange(categories.find((c) => c.id === value) ?? null);
  }, [value, categories, onCategoryChange]);

  return (
    <Select
      value={value ?? undefined}
      onValueChange={(id) => {
        onChange(id);
        onCategoryChange?.(categories.find((c) => c.id === id) ?? null);
      }}
      disabled={disabled || loading}
    >
      <SelectTrigger>
        <SelectValue placeholder={loading ? 'Loading categories…' : (placeholder ?? 'Pick a category…')} />
      </SelectTrigger>
      <SelectContent>
        {categories.length === 0 && !loading ? (
          <SelectItem value="__none__" disabled>No categories yet</SelectItem>
        ) : (
          categories.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name} <span className="text-muted-foreground text-[11px] font-mono ml-1">{c.code}</span>
              {c.kind !== 'general' && (
                <span className="text-coral-600 text-[11px] ml-1">· {c.kind === 'mileage' ? 'mileage' : 'per-diem'}</span>
              )}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
